/**
 * Page Performance console — response shapes (pure: zod only).
 *
 * The four reads are documented in the Phase 2 contract
 * (`Claude outputs/page-performance-phase2-contract.md` → Endpoints). They
 * are parsed LENIENTLY on purpose: a browser build and a server build are
 * never released in the same second, so
 *   - unknown fields are ignored (zod strips them),
 *   - every field is optional and a malformed one becomes null instead of
 *     throwing (`.catch`), so one odd value never takes the screen down,
 *   - a list may arrive as `rows`, `data` or `items`, with its paging flat
 *     or under `pagination`.
 * A field the screen needs and does not get renders as "—" or a stated
 * "not sent by the server" line, never as a crash.
 */
import { z } from 'zod'

const num = z.number().nullable().optional().catch(null)
const str = z.string().nullable().optional().catch(null)
const bool = z.boolean().nullable().optional().catch(null)

/** `{ v, prev }` — a bare number is accepted as `{ v: n, prev: null }`. */
const valuePair = z
  .preprocess(
    (x) => (typeof x === 'number' ? { v: x, prev: null } : x),
    z.object({ v: num, prev: num }),
  )
  .nullable()
  .optional()
  .catch(null)

/** An array whose bad elements are dropped instead of failing the whole. */
function lenientArray(item) {
  return z
    .array(z.unknown())
    .nullable()
    .optional()
    .catch([])
    .transform((arr) =>
      (arr ?? []).flatMap((x) => {
        const r = item.safeParse(x)
        return r.success ? [r.data] : []
      }),
    )
}

const trendPoint = z.object({ t: str, p95Ms: num, loads: num })

const scopeSchema = z
  .object({
    organization: str,
    organizationName: str,
    period: str,
    compare: str,
    from: str,
    to: str,
    tz: str,
    slot: str,
    daysAreUtc: bool,
    // true only when the window lies inside one UTC day (users = distinct people).
    usersExact: bool,
    compareFrom: str,
    compareTo: str,
    compareApproximate: bool,
    comparePartial: bool,
    lastRolledDay: str,
  })
  .nullable()
  .optional()
  .catch(null)

/** Kept as an opaque object — `normaliseLimits` (view-model.js) reads it. */
const limitsSchema = z.record(z.string(), z.unknown()).nullable().optional().catch(null)

const chartSchema = z
  .object({
    slots: lenientArray(z.object({ t: str, loads: num, avgMs: num, p95Ms: num })),
    restarts: lenientArray(z.string()),
  })
  .nullable()
  .optional()
  .catch(null)

/**
 * Why a row has its colour: `value` in figure units (ms / fraction), `limit`
 * as typed (s / %), each naming its unit. null when everything is inside.
 */
const statusReasonSchema = z
  .object({ metric: str, level: str, value: num, valueUnit: str, limit: num, limitUnit: str, route: str })
  .nullable()
  .optional()
  .catch(null)

export const pageRowSchema = z.object({
  route: z.string(),
  loads: num,
  // loads − abandoned: the opens every load-time figure is worked out from.
  measuredLoads: num,
  users: num,
  avgMs: num,
  p50Ms: num,
  p95Ms: num,
  p99Ms: num,
  ttfbP95Ms: num,
  lcpP75Ms: num,
  failedLoads: num,
  failedRate: num,
  abandoned: num,
  capped: num,
  waitedMs: num,
  lastOpenedAt: str,
  lastOpenedExact: bool,
  trend: lenientArray(trendPoint),
  status: str,
  statusReason: statusReasonSchema,
})

export const organisationRowSchema = z.object({
  tenantId: z.string(),
  name: str,
  pages: num,
  loads: num,
  measuredLoads: num,
  users: num,
  avgMs: num,
  p95Ms: num,
  p99Ms: num,
  failedLoads: num,
  failedRate: num,
  abandoned: num,
  slowestPage: z.object({ route: str, p95Ms: num }).nullable().optional().catch(null),
  status: str,
  statusReason: statusReasonSchema,
  trend: lenientArray(trendPoint),
})

const statusCountsSchema = z
  .object({ healthy: num, warning: num, slow: num, critical: num })
  .nullable()
  .optional()
  .catch(null)

/** Flatten the list envelope: rows under rows|data|items, paging flat or nested. */
function listEnvelope(raw) {
  if (!raw || typeof raw !== 'object') return {}
  const paging = raw.pagination && typeof raw.pagination === 'object' ? raw.pagination : {}
  return {
    ...raw,
    rows: raw.rows ?? raw.data ?? raw.items ?? [],
    total: raw.total ?? paging.total,
    page: raw.page ?? paging.page,
    limit: raw.limit ?? paging.limit,
    totalPages: raw.totalPages ?? paging.totalPages,
  }
}

function listSchema(row) {
  return z.preprocess(
    listEnvelope,
    z.object({
      rows: lenientArray(row),
      total: num,
      page: num,
      limit: num,
      totalPages: num,
      statusCounts: statusCountsSchema,
      scope: scopeSchema,
      limits: limitsSchema,
    }),
  )
}

const pagesListSchema = listSchema(pageRowSchema)
const organisationsListSchema = listSchema(organisationRowSchema)

const overviewSchema = z.object({
  scope: scopeSchema,
  limits: limitsSchema,
  health: z
    .object({
      pages: num,
      healthy: num,
      warning: num,
      slow: num,
      critical: num,
      notOpened: num,
      status: str,
    })
    .nullable()
    .optional()
    .catch(null),
  users: num,
  usersPrev: num,
  tiles: z
    .object({ loads: valuePair, avgMs: valuePair, p95Ms: valuePair, failedRate: valuePair })
    .nullable()
    .optional()
    .catch(null),
  chart: chartSchema,
  atAGlance: z
    .object({
      pagesOverLimit: num,
      pagesOpened: num,
      failedLoads: num,
      loads: num,
      abandoned: num,
      measuredLoads: num,
      thinData: bool,
    })
    .nullable()
    .optional()
    .catch(null),
})

const apiCallRowSchema = z.object({
  method: str,
  path: z.string(),
  perLoad: num,
  calls: num,
  failed: num,
  avgMs: num,
  p95Ms: num,
  firstByteMs: num,
  sizeBytes: num,
  failedRate: num,
  status: str,
  statusReason: statusReasonSchema,
})

const loadsP95 = z.object({ loads: num, p95Ms: num }).nullable().optional().catch(null)

const detailSchema = z.object({
  route: str,
  scope: scopeSchema,
  limits: limitsSchema,
  // null = no opens of this page in the scope.
  status: str,
  statusReason: statusReasonSchema,
  users: num,
  tiles: z
    .object({
      loads: valuePair,
      measuredLoads: valuePair,
      users: valuePair,
      avgMs: valuePair,
      p50Ms: valuePair,
      p75Ms: valuePair,
      p95Ms: valuePair,
      p99Ms: valuePair,
      abandoned: valuePair,
      capped: valuePair,
      failedRate: valuePair,
    })
    .nullable()
    .optional()
    .catch(null),
  browser: z
    .object({
      fullLoads: num,
      ttfbP95Ms: num,
      fcpP75Ms: num,
      lcpP75Ms: num,
      clsP75: num,
      inpP75Ms: num,
      failedRate: num,
      levels: z
        .object({ ttfbP95: str, lcpP75: str, errorRate: str })
        .nullable()
        .optional()
        .catch(null),
    })
    .nullable()
    .optional()
    .catch(null),
  thin: bool,
  // Ascending ms, ≤ 200; null for 30d / 90d, [] when there were none.
  opensForHelp: z.array(z.number()).nullable().optional().catch(null),
  // false when the points were read off the histogram (more than 200 opens).
  opensForHelpExact: bool,
  chart: chartSchema,
  whoAndHow: z
    .object({
      byDevice: lenientArray(z.object({ device: str, loads: num, p95Ms: num })),
      byBrowser: lenientArray(z.object({ browser: str, loads: num, p95Ms: num })),
      byOs: lenientArray(z.object({ os: str, loads: num })),
      full: loadsP95,
      spa: loadsP95,
      abandoned: z.object({ loads: num }).nullable().optional().catch(null),
      capped: z.object({ loads: num }).nullable().optional().catch(null),
    })
    .nullable()
    .optional()
    .catch(null),
  // Ignores the company filter; includes the selected company (`selected`).
  otherOrganizations: lenientArray(
    z.object({ tenantId: str, name: str, loads: num, users: num, avgMs: num, p95Ms: num, status: str, selected: bool }),
  ),
  apiCallsSummary: z
    .object({
      loads: num,
      endpoints: num,
      calls: num,
      callsPerLoad: num,
      slowest: z.object({ method: str, path: str, p95Ms: num }).nullable().optional().catch(null),
      from: str,
    })
    .nullable()
    .optional()
    .catch(null),
  // `apiCalls` is an array of rows, or `{ rows }`, or null when the period
  // is longer than the raw opens are kept (90 days).
  apiCalls: z
    .preprocess(
      (x) => (Array.isArray(x) ? { rows: x, available: true } : x),
      z.object({ rows: lenientArray(apiCallRowSchema), available: bool }),
    )
    .nullable()
    .optional()
    .catch(null),
})

/**
 * Accept both a bare body and the `{ data: { … } }` envelope some routes in
 * this API use. A list's `data` ARRAY is its rows, not an envelope, so only
 * an object under `data` is unwrapped.
 */
export function unwrapBody(raw) {
  if (raw && typeof raw === 'object' && raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
    return raw.data
  }
  return raw ?? {}
}

export function parseOverview(raw) {
  return overviewSchema.parse(unwrapBody(raw))
}

export function parsePagesList(raw) {
  return pagesListSchema.parse(unwrapBody(raw))
}

export function parseOrganisationsList(raw) {
  return organisationsListSchema.parse(unwrapBody(raw))
}

export function parseDetail(raw) {
  return detailSchema.parse(unwrapBody(raw))
}

/** Pages the list has, worked out when the server only sends `total`. */
export function totalPagesOf(list, limit) {
  if (list?.totalPages != null && list.totalPages > 0) return list.totalPages
  const total = list?.total ?? 0
  return Math.max(1, Math.ceil(total / Math.max(1, limit || 1)))
}
